import React from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Public as RegionsIcon,
  People as UsersIcon,
  Dashboard as DashboardIcon,
  Grain as GrapeIcon,
  Business as ProducerIcon,
  WineBar as WineNameIcon,
  // Venue Ops icons
  Store as VenueIcon,
  LocalShipping as SupplierIcon,
  Inventory as InventoryIcon,
  ShoppingCart as PurchasingIcon,
  Checklist as StockCountIcon,
  LocationOn as LocationIcon,
  RuleOutlined as GovernanceIcon,
  PointOfSale as PosIcon,
  LocalBar as MenuIcon,
  Receipt as SalesImportIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const drawerWidth = 280;

interface SideNavProps {
  open: boolean;
  onClose?: () => void;
  variant: 'permanent' | 'persistent' | 'temporary';
}

interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  showAlways: boolean;
}

const SideNav: React.FC<SideNavProps> = ({ open, onClose, variant }) => {
  const theme = useTheme();
  const location = useLocation();
  const { isSystemAdmin, isVenueUser } = useAuth();
  const venueUser = isVenueUser();

  // Temporary mode (iPad/tablet): drawer floats over content and auto-closes
  // when the user selects a nav item. Permanent mode (desktop) keeps the
  // existing in-flow card-style sidebar.
  const isTemporary = variant === 'temporary';
  const handleNavClick = () => {
    if (isTemporary && onClose) onClose();
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  // Detect venue context: any path matching /admin/venues/:id/... (where :id is not 'create')
  const venueMatch = location.pathname.match(/^\/admin\/venues\/([^/]+)/);
  const venueId = venueMatch?.[1];
  // Show venue sub-nav on any venue path including the dashboard root, but not on the venue edit page.
  const isVenueContext =
    !!venueId && venueId !== 'create' && !location.pathname.match(/^\/admin\/venues\/[^/]+\/edit\/?$/);

  // ── USERS ────────────────────────────────────────────────────────────────
  const userItems: NavItem[] = [
    { text: 'User Management', icon: <UsersIcon />, path: '/admin/users', showAlways: isSystemAdmin() },
  ];

  // ── WINE IDENTITY items ───────────────────────────────────────────────────
  const wineIdentityItems: NavItem[] = [
    { text: 'Dashboard',       icon: <DashboardIcon />, path: '/admin/dashboard',  showAlways: true },
    { text: 'Wines',           icon: <WineNameIcon />,  path: '/admin/wines',      showAlways: true },
    { text: 'Producers',       icon: <ProducerIcon />,  path: '/admin/producers',  showAlways: true },
    { text: 'Wine Regions',    icon: <RegionsIcon />,   path: '/admin/regions',    showAlways: true },
    { text: 'Grape Varieties', icon: <GrapeIcon />,     path: '/admin/grapes',     showAlways: true },
    { text: 'Wine Names',      icon: <WineNameIcon />,  path: '/admin/wine-names', showAlways: true },
  ];

  // ── WINE DATA GOVERNANCE items (subsection inside Wine Identity) ─────────
  const wineDataGovernanceItems: NavItem[] = [
    { text: 'Review Queue',    icon: <GovernanceIcon />, path: '/admin/governance/review-queue',    showAlways: true },
    { text: 'Duplicate Wines', icon: <GovernanceIcon />, path: '/admin/governance/duplicate-wines', showAlways: true },
  ];

  // ── INVENTORY GOVERNANCE global items ─────────────────────────────────────
  const inventoryGovernanceGlobalItems: NavItem[] = [
    { text: 'Repair',      icon: <GovernanceIcon />, path: '/admin/governance/repair',  showAlways: true },
    { text: 'Data Health', icon: <GovernanceIcon />, path: '/admin/governance/health',  showAlways: true },
  ];

  // ── INVENTORY global items ────────────────────────────────────────────────
  const inventoryItems: NavItem[] = [
    { text: 'Venues',    icon: <VenueIcon />,    path: '/admin/venues',    showAlways: true },
    { text: 'Suppliers', icon: <SupplierIcon />, path: '/admin/suppliers', showAlways: true },
  ];

  // ── Venue-scoped sub-nav items ────────────────────────────────────────────
  // Phase 4.H — Wine Lists removed from primary nav; the unified Menus entry
  // is now the single canonical surface for both wine-list and cocktail-list
  // workflows. Menu Recipes is also hidden here — consumption recipes are
  // storage-only and will move into an item-level "Advanced" surface later.
  // Legacy routes (/wine-lists, /menu-consumption-recipes) remain reachable
  // by direct URL during the migration window.
  const venueScopedItems = venueId
    ? [
        { text: 'Overview',     icon: <DashboardIcon />,  path: `/admin/venues/${venueId}` },
        { text: 'Menus',        icon: <MenuIcon />,       path: `/admin/venues/${venueId}/menus` },
        { text: 'Inventory',    icon: <InventoryIcon />,  path: `/admin/venues/${venueId}/inventory` },
        { text: 'Purchasing',   icon: <PurchasingIcon />, path: `/admin/venues/${venueId}/purchasing` },
        { text: 'Suppliers',    icon: <SupplierIcon />,   path: `/admin/venues/${venueId}/suppliers` },
        { text: 'Stock Counts',  icon: <StockCountIcon />,  path: `/admin/venues/${venueId}/stock-counts` },
        { text: 'Sales Imports', icon: <SalesImportIcon />, path: `/admin/venues/${venueId}/sales-imports` },
        { text: 'Locations',     icon: <LocationIcon />,    path: `/admin/venues/${venueId}/locations` },
      ]
    : [];

  // ── INVENTORY GOVERNANCE (venue-scoped) ───────────────────────────────────
  // POS Governance is admin-only on the backend (ledger triage), so hide the
  // link from VENUE_USER even inside their venue context — clicking it would
  // 403.
  const inventoryGovernanceItems = venueId && !venueUser
    ? [
        { text: 'POS Governance', icon: <PosIcon />, path: `/admin/venues/${venueId}/pos-inventory/governance` },
      ]
    : [];

  const renderNavItem = (item: NavItem | typeof venueScopedItems[0], _index: number, indent = false) => {
    if ('showAlways' in item && !item.showAlways) return null;
    const active = isActive(item.path);
    return (
      <ListItemButton
        component={Link as React.ElementType}
        to={item.path}
        key={item.text}
        selected={active}
        onClick={handleNavClick}
        sx={{
          mb: 0.5,
          borderRadius: 2,
          py: 1.125,
          px: indent ? 1 : 2,
          pl: indent ? 3 : 2,
          minHeight: 44,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            transform: 'translateX(4px)',
          },
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            color: theme.palette.primary.main,
            fontWeight: 600,
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
            '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 36,
            color: active ? theme.palette.primary.main : theme.palette.text.secondary,
            transition: 'color 0.2s ease-in-out',
            '& .MuiSvgIcon-root': { fontSize: indent ? '1.1rem' : '1.25rem' },
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.text}
          slotProps={{
            primary: {
              sx: {
                fontSize: indent ? '0.8125rem' : '0.875rem',
                fontWeight: active ? 600 : 500,
              },
            },
          }}
        />
      </ListItemButton>
    );
  };

  const SectionLabel: React.FC<{ label: string }> = ({ label }) => (
    <Typography
      variant="caption"
      sx={{
        px: 2,
        pt: 2,
        pb: 0.5,
        display: 'block',
        fontWeight: 700,
        fontSize: '0.7rem',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: alpha(theme.palette.text.secondary, 0.7),
      }}
    >
      {label}
    </Typography>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          // Temporary mode: full-height slide-in from the edge with no margin
          // so the drawer reads as an Apple-style off-canvas panel. Permanent
          // mode keeps the existing card-style sidebar with rounded corners,
          // a 32px gutter, and an inline 'static' position.
          ...(isTemporary
            ? {
                width: drawerWidth,
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
              }
            : {
                width: drawerWidth - 32,
                position: 'static',
                height: 'calc(100vh - 32px)',
                borderRadius: 2,
                m: 2,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                backdropFilter: 'blur(10px)',
              }),
          boxSizing: 'border-box',
          border: 'none',
          backgroundImage: 'none',
          backgroundColor: theme.palette.background.paper,
          overflowY: 'auto',
        },
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          p: 3,
          pt: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          minHeight: 80,
        }}
      >
        {/* Wine-identity / wine-database admin areas keep the Nota Wine logo;
            everywhere else the shell uses the general Nota Stock branding. */}
        {(() => {
          const wineIdentityPrefixes = [
            '/admin/regions',
            '/admin/grapes',
            '/admin/producers',
            '/admin/wine-names',
            '/admin/wines',
            '/admin/governance',
          ];
          const onWineIdentity = wineIdentityPrefixes.some((p) =>
            location.pathname.startsWith(p),
          );
          const src = onWineIdentity
            ? '/assets/logo-nota-wine.svg'
            : '/assets/logo-nota-stock.svg';
          const alt = onWineIdentity ? 'Nota Wine' : 'Nota Stock';
          return (
            <img
              src={src}
              alt={alt}
              style={{ height: '48px', width: 'auto', maxWidth: '100%' }}
            />
          );
        })()}
      </Box>

      {/* ── USERS section ────────────────────────────────────────────── */}
      <Box sx={{ px: 1, pt: 1 }}>
        {userItems.some(item => item.showAlways) && (
          <>
            <SectionLabel label="Users" />
            <List disablePadding sx={{ px: 1, pb: 1 }}>
              {userItems.map((item, i) => renderNavItem(item, i))}
            </List>
            <Divider sx={{ mx: 1, opacity: 0.5 }} />
          </>
        )}

        {/* ── WINE IDENTITY + Data Governance — hidden for VENUE_USER ── */}
        {!venueUser && (
          <>
            <SectionLabel label="Wine Identity" />
            <List disablePadding sx={{ px: 1, pb: 1 }}>
              {wineIdentityItems.map((item, i) => renderNavItem(item, i))}
            </List>

            <Typography
              variant="caption"
              sx={{
                px: 2,
                pt: 1.5,
                pb: 0.5,
                ml: 1,
                display: 'block',
                fontWeight: 700,
                fontSize: '0.65rem',
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                color: alpha(theme.palette.text.secondary, 0.5),
              }}
            >
              Data Governance
            </Typography>
            <List disablePadding sx={{ px: 1, pb: 1 }}>
              {wineDataGovernanceItems.map((item, i) => renderNavItem(item, i, true))}
            </List>

            <Divider sx={{ mx: 1, opacity: 0.5 }} />
          </>
        )}

        {/* ── INVENTORY / venue workspace section ─────────────────────── */}
        <SectionLabel label={venueUser ? 'Venue Workspace' : 'Inventory'} />
        <List disablePadding sx={{ px: 1, pb: 1 }}>
          {/* Global Venues/Suppliers entries hidden for VENUE_USER — they
              only operate inside the venue workspace they're already in. */}
          {!venueUser && inventoryItems.map((item, i) => renderNavItem(item, i))}

          {/* Venue-scoped sub-nav — shown when inside a venue workspace */}
          {isVenueContext && venueScopedItems.length > 0 && (
            <Box
              sx={{
                mt: 0.5,
                mb: 0.5,
                ml: venueUser ? 0 : 1,
                borderLeft: venueUser ? 'none' : `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                pl: venueUser ? 0 : 0.5,
              }}
            >
              {venueScopedItems.map((item, i) => renderNavItem(item, i, !venueUser))}
            </Box>
          )}

          {/* Empty state for VENUE_USER not currently inside a venue. */}
          {venueUser && !isVenueContext && (
            <ListItemButton
              component={Link as React.ElementType}
              to="/admin/my-venues"
              onClick={handleNavClick}
              sx={{
                mb: 0.5,
                borderRadius: 2,
                py: 1.125,
                px: 2,
                minHeight: 44,
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <VenueIcon />
              </ListItemIcon>
              <ListItemText
                primary="Select a venue"
                slotProps={{
                  primary: { sx: { fontSize: '0.875rem', fontWeight: 500 } },
                }}
              />
            </ListItemButton>
          )}
        </List>

        {/* ── INVENTORY GOVERNANCE — global items hidden for VENUE_USER ── */}
        {(!venueUser || (isVenueContext && inventoryGovernanceItems.length > 0)) && (
          <>
            <SectionLabel label="Inventory Governance" />
            <List disablePadding sx={{ px: 1, pb: 1 }}>
              {!venueUser && inventoryGovernanceGlobalItems.map((item, i) => renderNavItem(item, i))}
              {isVenueContext && inventoryGovernanceItems.map((item, i) => renderNavItem(item, i))}
            </List>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default SideNav;
